import { useNavigate } from 'react-router-dom';
import { CaretLeft } from '@phosphor-icons/react';
import styled from 'styled-components';
import { useMemo } from 'react';
import { HoverBorderButton } from '../elements/Button';
import { theme } from '../utility';

interface BackButtonProps {
  moveTo?: string; // string for URL, number for relative navigation
}

const StyledBackButton = styled(HoverBorderButton)`
  min-width: 40px;
`;

export function BackButton({ moveTo }: BackButtonProps) {
  const navigate = useNavigate();

  const moveBack = () => {
    if (moveTo) {
      navigate(moveTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <StyledBackButton className="icon-btn mr-3" onClick={moveBack} type="button">
      <CaretLeft size={20} />
    </StyledBackButton>
  );
}

const SmallBackBtn = styled.div`
  width: 30px;
  height: 30px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.grey};
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
`;

interface TitleBackButtonProps {
  url?: string | null;
}

export function TitleBackButton({ url = null }: TitleBackButtonProps) {
  const navigate = useNavigate();

  const moveBack = () => {
    if (url) {
      navigate(url);
    } else {
      navigate(-1);
    }
  };

  return (
    <SmallBackBtn className="mr-2" onClick={moveBack} role="button" tabIndex={0}>
      <CaretLeft size={15} />
    </SmallBackBtn>
  );
}

const AllocationProgressBarContainer = styled.div`
  margin: 0.8rem auto;
  position: relative;
  height: 8px;
  width: 100%;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors.box};
  &.lighterBg {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

interface AllocationProgressBarProps {
  progress: number;
  bgColor: string;
}

const AllocationProgressBar = styled.div<AllocationProgressBarProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ progress }) => progress}%;
  height: 100%;
  border-radius: inherit;
  background-color: ${({ bgColor }) => bgColor};
`;

interface ProgressBarProps {
  progress: number;
  className?: string;
  customColor?: string;
}

export function ProgressBar({ progress, className = '', customColor }: ProgressBarProps) {
  const bgColor = useMemo(() => {
    if (customColor) {
      return customColor;
    }
    return progress === 100 ? theme.colors.green : theme.colors.danger;
  }, [progress, customColor]);

  return (
    <AllocationProgressBarContainer className={className}>
      <AllocationProgressBar bgColor={bgColor} progress={progress} />
    </AllocationProgressBarContainer>
  );
}
